import { Button, Card, Input } from '@heroui/react'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Coins, ShieldAlert, Sparkles, UserCheck } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'
import { useHandleError } from '~/hooks/use-handle-error'
import { authApiClient } from '~/lib/api-client'

export const Route = createFileRoute('/admin/billing')({
  component: AdminBillingPage,
})

function AdminBillingPage() {
  const handleError = useHandleError()

  // Form State 1: Credit Adjustment
  const [adjustUserId, setAdjustUserId] = React.useState('')
  const [creditsDelta, setCreditsDelta] = React.useState<number>(100)
  const [adjustReason, setAdjustReason] = React.useState('')

  // Form State 2: Tier Override
  const [overrideUserId, setOverrideUserId] = React.useState('')
  const [selectedTier, setSelectedTier] = React.useState<'FREE' | 'EXPLORER' | 'PRO' | 'PREMIUM'>('PRO')
  const [customCreditsMonthly, setCustomCreditsMonthly] = React.useState('')
  const [expiresInDays, setExpiresInDays] = React.useState('')
  const [overrideReason, setOverrideReason] = React.useState('')

  // Mutation 1: Adjust Credits
  const adjustMutation = useMutation({
    mutationFn: async () => {
      const res = await authApiClient.admin.billing['adjust-credits'].$post({
        json: {
          userId: adjustUserId.trim(),
          creditsDelta: Number(creditsDelta),
          reason: adjustReason.trim(),
        },
        header: { Authorization: '' },
      })
      if (!res.ok) throw res
      return await res.json()
    },
    onSuccess: (data) => {
      toast.success(data.message || '信用积分调整成功')
      // Reset form on success except user id
      setAdjustReason('')
    },
    onError: handleError,
  })

  // Mutation 2: Tier Override
  const overrideMutation = useMutation({
    mutationFn: async () => {
      const res = await authApiClient.admin.billing['override-tier'].$post({
        json: {
          userId: overrideUserId.trim(),
          tier: selectedTier,
          reason: overrideReason.trim(),
          creditsMonthly: customCreditsMonthly ? Number(customCreditsMonthly) : undefined,
          expiresInDays: expiresInDays ? Number(expiresInDays) : undefined,
        },
        header: { Authorization: '' },
      })
      if (!res.ok) throw res
      return await res.json()
    },
    onSuccess: (data) => {
      toast.success(data.message || '会员等级覆写成功，该用户的活跃 Session 已被强制清除')
      // Reset form on success except user id
      setOverrideReason('')
      setCustomCreditsMonthly('')
      setExpiresInDays('')
    },
    onError: handleError,
  })

  const handleAdjustSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!adjustUserId.trim()) {
      toast.error('请输入用户 ID')
      return
    }
    if (isNaN(creditsDelta) || creditsDelta === 0) {
      toast.error('请输入有效的积分变动值')
      return
    }
    if (creditsDelta < -1000 || creditsDelta > 1000) {
      toast.error('积分变动范围必须在 [-1000, 1000] 之间')
      return
    }
    if (!adjustReason.trim()) {
      toast.error('请填写调整原因')
      return
    }
    adjustMutation.mutate()
  }

  const handleOverrideSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!overrideUserId.trim()) {
      toast.error('请输入用户 ID')
      return
    }
    if (!overrideReason.trim()) {
      toast.error('请填写覆写原因')
      return
    }
    overrideMutation.mutate()
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
          <Sparkles className="size-8 text-primary animate-pulse" />
          充值与会员管理
        </h1>
        <p className="text-muted mt-1 font-medium">手动增加或扣除用户的购买积分，或者强制覆写用户的订阅等级与可用时长。</p>
      </div>

      {/* Forms Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Card 1: Adjust Credits */}
        <Card className="p-6 border border-divider bg-content1/50 backdrop-blur-sm shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-warning/10 text-warning">
                <Coins className="size-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold">信用积分调整</h3>
                <p className="text-xs text-muted-foreground mt-0.5">直接修改用户积分余额。支持扣除（负值）。</p>
              </div>
            </div>

            <form onSubmit={handleAdjustSubmit} className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground/80">目标用户 ID</label>
                <Input
                  required
                  placeholder="e.g. user_xxx"
                  value={adjustUserId}
                  onChange={(e) => setAdjustUserId(e.target.value)}
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground/80">积分变动值 (Credits Delta)</label>
                <div className="flex gap-2 items-center">
                  <Input
                    required
                    type="number"
                    min={-1000}
                    max={1000}
                    placeholder="e.g. 100 or -50"
                    value={String(creditsDelta)}
                    onChange={(e) => setCreditsDelta(Number(e.target.value))}
                  />
                </div>
                {/* Presets */}
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {[-100, -50, 50, 100, 200, 500].map((preset) => (
                    <Button
                      key={preset}
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-xs h-7 min-w-0 px-2"
                      onPress={() => setCreditsDelta(preset)}
                    >
                      {preset > 0 ? `+${preset}` : preset}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  范围 [-1000, 1000]。正数代表充值/赠送，负数代表扣减。
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground/80">调整原因 (Reason)</label>
                <Input
                  required
                  placeholder="请输入本次积分调整的合理解释，以便审计留存"
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                />
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full font-bold shadow-md shadow-primary/20"
                  isDisabled={adjustMutation.isPending}
                >
                  {adjustMutation.isPending ? '正在提交调整...' : '确认调整积分'}
                </Button>
              </div>
            </form>
          </div>
        </Card>

        {/* Card 2: Tier Override */}
        <Card className="p-6 border border-divider bg-content1/50 backdrop-blur-sm shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 rounded-lg bg-primary/10 text-primary">
                <UserCheck className="size-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold">订阅等级与限额覆写</h3>
                <p className="text-xs text-muted-foreground mt-0.5">强制覆盖用户的会籍与月度免费额度，撤销当前 Session。</p>
              </div>
            </div>

            <form onSubmit={handleOverrideSubmit} className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground/80">目标用户 ID</label>
                <Input
                  required
                  placeholder="e.g. user_xxx"
                  value={overrideUserId}
                  onChange={(e) => setOverrideUserId(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-sm font-semibold text-foreground/80">目标会员等级</label>
                  <select
                    className="w-full h-10 bg-content2 border border-divider rounded-medium px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-medium"
                    value={selectedTier}
                    onChange={(e) => setSelectedTier(e.target.value as any)}
                  >
                    <option value="FREE">FREE</option>
                    <option value="EXPLORER">EXPLORER</option>
                    <option value="PRO">PRO</option>
                    <option value="PREMIUM">PREMIUM</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-sm font-semibold text-foreground/80">有效期时长 (天)</label>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    placeholder="默认: Explorer 7天, 其他 30天"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground/80">自定义月度免费积分 (选填)</label>
                <Input
                  type="number"
                  placeholder="留空则使用对应等级的标准额度"
                  value={customCreditsMonthly}
                  onChange={(e) => setCustomCreditsMonthly(e.target.value)}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  各等级标准额度: Explorer 300, Pro 700, Premium 1500。
                </p>
              </div>

              <div className="space-y-1">
                <label className="text-sm font-semibold text-foreground/80">操作审计原因 (Reason)</label>
                <Input
                  required
                  placeholder="请输入本次会籍变更的原因，以便审计"
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                />
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  variant="primary"
                  className="w-full font-bold shadow-md shadow-primary/20"
                  isDisabled={overrideMutation.isPending}
                >
                  {overrideMutation.isPending ? '正在提交变更...' : '确认覆写会员等级'}
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </div>

      {/* Security Tip Banner */}
      <Card className="p-4 border border-danger/20 bg-danger/5 text-danger flex flex-row items-center gap-3">
        <ShieldAlert className="size-5 shrink-0" />
        <div className="text-xs font-semibold leading-relaxed">
          <strong>安全警示与合规提醒</strong>：所有在此执行的操作均会与当前登录的管理员 ID ({Route.useRouteContext().session?.user?.email}) 关联，并作为审计日志永久记录在系统数据库的
          <code className="mx-1 px-1 bg-danger/10 rounded">billing_adjustments</code>
          表中。操作前请确保该需求已经得到财务及用户协议的审核。
        </div>
      </Card>
    </div>
  )
}
